import {
  format,
  isValid as isValidDate,
  parse as parseBR,
  parseISO,
} from 'date-fns';

/**
 * Normaliza qualquer tipo de entrada (string, objeto moment, Date nativa, etc.)
 * para uma instância válida de `Date` (instante absoluto em tempo).
 *
 * Regras de funcionamento:
 *  - Se o valor for `null`, `undefined` ou falsy → retorna `null`.
 *  - Se for um objeto com método `.toDate()` (ex: objeto do `moment`) → chama `toDate()`
 *    e valida o resultado.
 *  - Se já for um `Date` nativo válido → retorna direto.
 *  - Se for string em formato ISO (`2025-08-25T19:25:00.002Z`) → parseia com `parseISO()`.
 *  - Se for string no padrão brasileiro `"dd/MM/yyyy HH:mm:ss"` → interpreta no
 *    fuso horário local via `parseBR()`.
 *  - Caso seja uma string em outros formatos (ex: `"Tue Aug 25 2025 ..."`) → tenta
 *    criar um `Date` nativo direto.
 *  - Se nenhuma das opções for válida → retorna `null`.
 *
 * Isso garante que a aplicação consiga lidar de forma robusta com datas vindas de
 * diferentes formatos e bibliotecas, sempre convergindo para um `Date` nativo.
 */
export function coerceToDate(input: any): Date | null {
  if (!input) return null;

  // moment()
  if (
    input &&
    typeof input === 'object' &&
    typeof input.toDate === 'function'
  ) {
    const d = input.toDate();
    return isValidDate(d) ? d : null;
  }

  // Date nativo
  if (input instanceof Date && isValidDate(input)) return input;

  // string ISO?
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(input)) {
    const d = parseISO(input);
    return isValidDate(d) ? d : null;
  }

  // string BR "dd/MM/yyyy HH:mm:ss"?
  if (typeof input === 'string' && /\d{2}\/\d{2}\/\d{4}/.test(input)) {
    const d = parseBR(input, 'dd/MM/yyyy HH:mm:ss', new Date()); // interpreta no fuso LOCAL
    return isValidDate(d) ? d : null;
  }

  // fallback para strings tipo "Tue Aug ..."
  if (typeof input === 'string') {
    const d = new Date(input);
    return isValidDate(d) ? d : null;
  }

  return null;
}

// 2) Só para exibir/erro em fuso local (sem usar date-fns-tz)
export function displayLocal(input: any): string {
  // se já é string BR, mantém
  if (typeof input === 'string' && /\d{2}\/\d{2}\/\d{4}/.test(input))
    return input;

  const d = coerceToDate(input);
  if (!d) return String(input ?? '');
  // mostra em local: dd/MM/yyyy HH:mm:ss
  return format(d, 'dd/MM/yyyy HH:mm:ss');
}

export const ts = (d: Date) => d.getTime();
